import { useMutation, useQuery } from '@tanstack/react-query'
import { AgGridReact } from 'ag-grid-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Col, Row, Tab, Tabs } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { buildAlertWithLocalizedMessage } from '../actions'
import { getLocaleText } from '../actions/datagrid'
import {
	eSalaryGet,
	finagoTimeEventMappingsQuery,
	groupsGetAllQuery,
	personsGetAllQuery,
	procountorWorkHoursAndOvertimesQuery,
	timeTrackingEntriesQuery,
	timeTrackingWorkHoursAndOvertimesSync,
} from '../api'
import { Button } from '../common/Button.tsx'
import {
	contextMenu,
	defaultColDef,
	gridSettings,
	loadViewState,
	restoreViewState,
	saveViewState,
	sideBar,
	statusBar,
} from '../common/grid'
import { restoreIconString } from '../common/icons'
import { PdfReport } from '../common/pdfreport'
import { fetchSettings } from '../common/settings'
import { dateFormatter, eSalaryDateGetter, jsError, numberFormatterFraction } from '../common/utils'
import { queryClient } from '../main'
import {
	absenceNS,
	translatedCausecode,
} from '../mgmtview/personalprofile/newabscencemodal/newabscencemodal.constants.js'
import { getHrEventTypeCode } from '../organizationview/integrations/modals/SettingsTime/Utils.js'
import { activeIntegrationsSelector, authSelector } from '../reducers'

const endpoint = 'workhours'
const viewStateKey = 'workHoursViewState'

const initialState = {
	columnVisibility: {
		hiddenColIds: [],
	},
}

let isFetching = false

export function WorkhoursView() {
	const { t, i18n } = useTranslation()
	const { t: tAbsence } = useTranslation(absenceNS)
	const resolvedLanguage = i18n.resolvedLanguage
	const dispatch = useDispatch()

	const gridRef = useRef()
	const timeGridRef = useRef()

	const activeIntegrations = useSelector(activeIntegrationsSelector)
	const isProcountorActive = !!activeIntegrations.tokenProcountor
	const isFinagoTimeActive = !!activeIntegrations.credentialsFinagoTime

	const [_timeReady, setTimeReady] = useState(false)

	const timeViewStateKey = 'timeWorkhoursViewState'

	const [activeTab, setActiveTab] = useState('payroll')

	const [settings, setSettings] = useState()

	const { companyName, salary } = useSelector(authSelector)

	const [report, setReport] = useState(null)
	const closeReport = () => setReport(null)

	const [fetching, setFetching] = useState(false)
	const [data, setData] = useState(JSON.parse(localStorage.getItem(endpoint)))

	const { data: timeData, refetch: refetchTimeData } = useQuery({
		...timeTrackingEntriesQuery('WORK_HOURS_AND_OVERTIMES'),
		enabled: isFinagoTimeActive,
	})

	const { data: timeEventMappings = [] } = useQuery({
		...finagoTimeEventMappingsQuery,
		enabled: isFinagoTimeActive,
	})

	useEffect(() => {
		if (timeData) {
			setTimeReady(true)
		}
	}, [timeData])

	const timeSyncMutation = useMutation({
		mutationFn: timeTrackingWorkHoursAndOvertimesSync,
	})

	const refreshTimeWorkhours = useCallback(async () => {
		await timeSyncMutation.mutateAsync()
		await refetchTimeData()
	}, [timeSyncMutation.mutateAsync, refetchTimeData])

	const didRun = useRef(false)

	useEffect(() => {
		if (!isFinagoTimeActive) return

		if (didRun.current) return
		didRun.current = true

		refreshTimeWorkhours()
	}, [refreshTimeWorkhours, isFinagoTimeActive])

	const proQ = useQuery({
		...procountorWorkHoursAndOvertimesQuery(resolvedLanguage),
		enabled: isProcountorActive,
	})

	const update = useCallback(async () => {
		if (isProcountorActive) return queryClient.invalidateQueries(procountorWorkHoursAndOvertimesQuery)
		if (!isFetching) {
			isFetching = true
			setFetching(true)
			try {
				const res = await eSalaryGet(endpoint, { language: resolvedLanguage })
				const json = res.data.workhours_response[endpoint] ?? []
				json.forEach((item) => {
					delete item.id
				})
				localStorage.setItem(endpoint, JSON.stringify(json))
				setData(json)
			} catch (err) {
				jsError(err)
				dispatch(buildAlertWithLocalizedMessage('ERROR', 'StatisticsFetchError', 'Virhe tilaston haussa'))
			} finally {
				isFetching = false
				setFetching(false)
			}
		}
	}, [isProcountorActive, resolvedLanguage, dispatch])

	useEffect(() => {
		;(async () => {
			setSettings(await fetchSettings())
			if (!isProcountorActive && !data) {
				update()
			}
		})()
	}, [isProcountorActive, data, update])

	const rows = isProcountorActive ? (Array.isArray(proQ.data) ? proQ.data : []) : Array.isArray(data) ? data : []

	const loading = isProcountorActive ? proQ.isLoading || proQ.isFetching : fetching

	const { data: personsData } = useQuery(personsGetAllQuery)
	const { data: groupsData } = useQuery(groupsGetAllQuery)

	const groupsMap = useMemo(() => {
		if (!groupsData) return {}
		return Object.fromEntries(groupsData.map((g) => [g.id, g.name]))
	}, [groupsData])

	const payGroupByNr = useMemo(() => {
		if (!personsData?.data) return {}
		return Object.fromEntries(personsData.data.map((p) => [String(p.nr), p.controlDetails?.payGroup ?? '']))
	}, [personsData])

	const employeeGroupByNr = useMemo(() => {
		if (!personsData?.data) return {}
		return Object.fromEntries(
			personsData.data.map((p) => [String(p.nr), groupsMap[p.controlDetails?.employeeGroupId] ?? '']),
		)
	}, [personsData, groupsMap])

	const mappedData = useMemo(
		() =>
			rows.map((row) => ({
				...row,
				payGroup: payGroupByNr[String(row.personnumber)] ?? '',
				employeeGroup: employeeGroupByNr[String(row.personnumber)] ?? '',
			})),
		[rows, payGroupByNr, employeeGroupByNr],
	)

	const procountorColumnDefs = !settings
		? undefined
		: [
				{
					field: 'personnumber',
					headerName: t('PersonNumber', 'Henkilönumero'),
				},
				{
					field: 'personname',
					headerName: t('Name', 'Nimi'),
					filter: 'agMultiColumnFilter',
				},
				{
					field: 'payGroup',
					headerName: t('PayGroup', 'Maksuryhmä'),
					filter: 'agMultiColumnFilter',
				},
				{
					field: 'employeeGroup',
					headerName: t('EmployeeGroup', 'Työntekijäryhmä'),
					filter: 'agMultiColumnFilter',
				},
				{
					field: 'paytype',
					headerName: t('PayType', 'Palkkalaji'),
					filter: 'agMultiColumnFilter',
				},
				{
					field: 'paytypename',
					headerName: t('PayTypeName', 'Palkkalajin nimi'),
					filter: 'agMultiColumnFilter',
				},
				{
					field: 'hours',
					headerName: t('Hours', 'Tuntia'),
					valueFormatter: numberFormatterFraction,
				},
				{
					field: 'sum',
					headerName: t('Sum', 'Summa'),
					valueFormatter: numberFormatterFraction,
				},
				{
					field: 'period',
					headerName: t('PaymentPeriod', 'Maksukausi'),
					filter: 'agMultiColumnFilter',
				},
				{
					field: 'startdate',
					headerName: t('PeriodStartDate', 'Palkkakauden alkupäivä'),
					cellClass: 'dateType',
					filter: 'agDateColumnFilter',
					valueGetter: eSalaryDateGetter,
					valueFormatter: dateFormatter,
				},
				{
					field: 'enddate',
					headerName: t('PeriodEndDate', 'Palkkakauden päättymispäivä'),
					cellClass: 'dateType',
					filter: 'agDateColumnFilter',
					valueGetter: eSalaryDateGetter,
					valueFormatter: dateFormatter,
				},
				{
					field: 'paydate',
					headerName: t('PayDate', 'Maksupäivä'),
					cellClass: 'dateType',
					filter: 'agDateColumnFilter',
					valueGetter: eSalaryDateGetter,
					valueFormatter: dateFormatter,
				},
			]

	const columnDefs = !settings
		? undefined
		: [
				...procountorColumnDefs,
				{
					field: 'dim1',
					filter: 'agMultiColumnFilter',
					headerName: settings.dim1name,
					valueGetter: (params) => params.data && settings.dim1map?.[params.data.dim1],
				},
				{
					field: 'dim2',
					filter: 'agMultiColumnFilter',
					headerName: settings.dim2name,
					valueGetter: (params) => params.data && settings.dim2map?.[params.data.dim2],
				},
				{
					field: 'dim3',
					filter: 'agMultiColumnFilter',
					headerName: settings.dim3name,
					valueGetter: (params) => params.data && settings.dim3map?.[params.data.dim3],
				},
				{
					field: 'dim4',
					filter: 'agMultiColumnFilter',
				},
				{
					field: 'dim5',
					filter: 'agMultiColumnFilter',
				},
			]

	const timeColumnDefs = [
		{
			headerName: t('ID', 'Tunniste'),
			field: 'id',
			width: 90,
			sortable: true,
			filter: 'agNumberColumnFilter',
			hide: true,
		},
		{
			headerName: t('Name', 'Nimi'),
			field: 'employeeName',
			filter: 'agMultiColumnFilter',
		},
		{
			headerName: t('EmployeeSourceID', 'Työntekijän lähdetunniste'),
			field: 'employeeSourceId',
			filter: 'agMultiColumnFilter',
			hide: true,
		},
		{
			headerName: t('SourceID', 'Lähdetunniste'),
			field: 'sourceId',
			filter: 'agMultiColumnFilter',
			hide: true,
		},
		{
			headerName: t('EventType', 'Tapahtumatyyppi'),
			filter: 'agMultiColumnFilter',
			valueGetter: (params) => {
				const code = getHrEventTypeCode(params.data?.eventTypeId, timeEventMappings)
				return code ? translatedCausecode(code, tAbsence) : ''
			},
		},
		{
			headerName: t('TimeEventType', 'Time-tapahtuma'),
			field: 'eventTypeName',
			filter: 'agMultiColumnFilter',
		},
		{
			headerName: t('EventTypeID', 'Tapahtuman tyyppitunniste'),
			field: 'eventTypeId',
			filter: 'agNumberColumnFilter',
			hide: true,
		},
		{
			headerName: t('Hours', 'Tunnit'),
			field: 'durationInHours',
			filter: 'agNumberColumnFilter',
			sortable: true,
			valueFormatter: (p) => (p.value != null ? numberFormatterFraction(p) : null),
		},
		{
			headerName: t('StartTime', 'Aloitusaika'),
			field: 'startTime',
			cellClass: 'dateType',
			filter: 'agDateColumnFilter',
			valueGetter: (params) => (params.data?.startTime ? new Date(params.data.startTime) : null),
			valueFormatter: dateFormatter,
		},
		{
			headerName: t('EndTime', 'Lopetusaika'),
			field: 'endTime',
			cellClass: 'dateType',
			filter: 'agDateColumnFilter',
			valueGetter: (params) => (params.data?.endTime ? new Date(params.data.endTime) : null),
			valueFormatter: dateFormatter,
		},
		{
			headerName: t('ApprovedByEmployee', 'Työntekijän hyväksymä'),
			field: 'approvedByEmployee',
			filter: 'agSetColumnFilter',
			valueFormatter: (params) => (params.value ? t('Yes', 'Kyllä') : t('No', 'Ei')),
		},
		{
			headerName: t('ApprovedByCustomer', 'Asiakkaan hyväksymä'),
			field: 'approvedByCustomer',
			filter: 'agSetColumnFilter',
			valueFormatter: (params) => (params.value ? t('Yes', 'Kyllä') : t('No', 'Ei')),
		},
		{
			headerName: t('Deleted', 'Poistettu'),
			field: 'deleted',
			filter: 'agSetColumnFilter',
			valueFormatter: (params) => (params.value ? t('Yes', 'Kyllä') : t('No', 'Ei')),
			hide: true,
		},
		{
			headerName: t('SourceDeleted', 'Poistettu lähteestä'),
			field: 'sourceDeleted',
			filter: 'agSetColumnFilter',
			valueFormatter: (params) => (params.value ? t('Yes', 'Kyllä') : t('No', 'Ei')),
			hide: true,
		},
		{
			headerName: t('WorkUnit', 'Työkohde'),
			field: 'workUnitName',
			filter: 'agMultiColumnFilter',
			hide: true,
		},
		{
			headerName: t('WorkUnitID', 'Työkohteen tunniste'),
			field: 'workUnitId',
			filter: 'agNumberColumnFilter',
			hide: true,
		},
	]

	if (!salary && columnDefs) {
		columnDefs.splice(5, 1) // remove sum column
	}

	const getContextMenuItems = useCallback(
		(params) => {
			let items = []

			items.push({
				name: t('DefaultGridValues', 'Palauta taulukon oletusasetukset'),
				action: () => {
					restoreViewState(viewStateKey, initialState)
				},
				icon: restoreIconString,
			})

			items.push('separator')

			items = items.concat(
				contextMenu(t, params, setReport, {
					title: t('WorkinghoursAndOverTime', 'Työtunnit ja ylityöt'),
					companyname: companyName,
				}),
			)
			return items
		},
		[t, companyName],
	)

	const getTimeContextMenuItems = useCallback(
		(params) => {
			let items = []

			items.push({
				name: t('DefaultGridValues', 'Palauta taulukon oletusasetukset'),
				action: () => {
					restoreViewState(timeViewStateKey, initialState)
				},
				icon: restoreIconString,
			})

			items.push('separator')

			items = items.concat(
				contextMenu(t, params, setReport, {
					title: t('WorkinghoursAndOverTime', 'Työtunnit ja ylityöt'),
				}),
			)

			return items
		},
		[t],
	)

	const handleTabSelect = (activeKey) => {
		setActiveTab(activeKey)

		if (activeKey === 'payroll' && gridRef.current?.api) {
			gridRef.current.api.sizeColumnsToFit()
		} else if (activeKey === 'time' && isFinagoTimeActive && timeGridRef.current?.api) {
			timeGridRef.current.api.sizeColumnsToFit()
			setTimeReady(false)
		}
	}

	return (
		<>
			{report !== null && <PdfReport json={report} onCloseReport={closeReport} />}

			<Row className="my-1">
				<Col sm="4">
					<h2 className="viewheading">{t('WorkinghoursAndOverTime', 'Työtunnit ja ylityöt')}</h2>
				</Col>
				<Col sm="8" align="right">
					{activeTab === 'payroll' && (
						<Button testId="update" variant="primary" disabled={fetching} onClick={update}>
							{t('Update', 'Päivitä')}
						</Button>
					)}

					{isFinagoTimeActive && activeTab === 'time' && (
						<Button testId="update-time" variant="primary" onClick={refreshTimeWorkhours}>
							{t('Update', 'Päivitä')}
						</Button>
					)}
				</Col>
			</Row>

			<Tabs defaultActiveKey="payroll" onSelect={handleTabSelect} className="mt-3">
				<Tab eventKey="payroll" title={t('FromPayroll', 'Palkanlaskennasta')}>
					<Row className="ag-theme-material styles-ag flex-grow-1 mt-3">
						<AgGridReact
							{...gridSettings}
							loading={loading}
							ref={gridRef}
							columnDefs={isProcountorActive ? procountorColumnDefs : columnDefs}
							defaultColDef={{ ...defaultColDef, filter: 'agNumberColumnFilter' }}
							sideBar={sideBar}
							rowData={mappedData}
							getContextMenuItems={getContextMenuItems}
							statusBar={statusBar}
							getLocaleText={(params) => getLocaleText(t, params)}
							animateRows
							suppressCellFocus
							enableCellTextSelection
							initialState={loadViewState(viewStateKey) || initialState}
							onStateUpdated={({ state }) => saveViewState(state, viewStateKey)}
							debug={import.meta.env.DEV}
						/>
					</Row>
				</Tab>

				{isFinagoTimeActive && (
					<Tab eventKey="time" title={t('FromTimeTracking', 'Time työajanseurannasta')}>
						<Row className="ag-theme-material styles-ag flex-grow-1 mt-3">
							<AgGridReact
								{...gridSettings}
								loading={!timeData}
								ref={timeGridRef}
								columnDefs={timeColumnDefs}
								defaultColDef={{ ...defaultColDef, filter: 'agNumberColumnFilter' }}
								sideBar={sideBar}
								rowData={timeData}
								getContextMenuItems={getTimeContextMenuItems}
								statusBar={statusBar}
								getLocaleText={(params) => getLocaleText(t, params)}
								animateRows
								suppressCellFocus
								enableCellTextSelection
								initialState={loadViewState(timeViewStateKey) || initialState}
								onStateUpdated={({ state }) => saveViewState(state, timeViewStateKey)}
								debug={import.meta.env.DEV}
							/>
						</Row>
					</Tab>
				)}
			</Tabs>
		</>
	)
}
